<script setup lang="ts">
import AppContextPanel from '@/components/app-shell/AppContextPanel.vue'
import CompanyCreateDialog from '@/components/companies/CompanyCreateDialog.vue'
import FinestButton from '@/components/FinestButton.vue'
import FinestStepNav from '@/components/FinestStepNav.vue'
import PersonOrCompanyStep, { type PartyOption } from '@/components/PersonOrCompanyStep.vue'
import PropertyCreateAssistant from '@/components/portfolio/PropertyCreateAssistant.vue'
import PropertyCreateDone from '@/components/portfolio/PropertyCreateDone.vue'
import PropertyLocationStep from '@/components/portfolio/PropertyLocationStep.vue'
import PropertyTypeStep from '@/components/portfolio/PropertyTypeStep.vue'
import PropertyUnitsStep from '@/components/portfolio/PropertyUnitsStep.vue'
import type { ExtendedEnum } from '@/types'
import EstateObjectsController from '@controllers/Investors/EstateObjectsController'
import FinancingRequestWizardController from '@controllers/Investors/FinancingRequestWizardController'
import { Head, useForm } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  DONE_STEP,
  LAST_FORM_STEP,
  LOCATION_STEP,
  OWNER_STEP,
  TYPE_STEP,
  usePropertyCreateSteps,
} from './usePropertyCreateSteps'
import { usePropertySpaceSize } from './usePropertySpaceSize'
import Bundesland = App.Enums.Bundesland
import EstateObjectData = App.Data.EstateObject.EstateObjectData
import EstateObjectType = App.Enums.EstateObject.EstateObjectType
import PersonType = App.Enums.PersonType

const props = defineProps<{
  ownerOptions: PartyOption[]
  estateObjectTypes: (ExtendedEnum<EstateObjectType> & { has_living_space: boolean })[]
  states: { value: Bundesland; label: string }[]
  createdEstateObject: EstateObjectData | null
  forFinancingRequest: boolean
}>()

const { t } = useI18n()

const created = computed(() => props.createdEstateObject)

const form = useForm({
  owner_type: (created.value?.owner_type ?? null) as PersonType | null,
  owner_id: created.value?.owner_id ?? null,
  estate_object_type: created.value?.estate_object_type ?? null,
  google_place_id: created.value?.google_place_id ?? null,
  street: created.value?.street ?? null,
  house_number: created.value?.house_number ?? null,
  address_addition: created.value?.address_addition ?? null,
  zip_code: created.value?.zip_code ?? null,
  city: created.value?.city ?? null,
  state: created.value?.state ?? null,
  living_space_size: created.value?.living_space_size ?? null,
  business_space_size: created.value?.business_space_size ?? null,
  number_of_units: created.value?.number_of_units ?? null,
})

const { step, locked, stepErrors, canReturnTo, goBackTo, advance, showFirstRejectedStep } = usePropertyCreateSteps(
  () => created.value?.id,
  () => form.errors,
)

const hasLivingSpace = computed(
  () => props.estateObjectTypes.find((option) => option.id === form.estate_object_type)?.has_living_space ?? true,
)

const spaceLabel = computed(() =>
  hasLivingSpace.value ? t('fields.living_space_size') : t('fields.business_space_size'),
)

const spaceSize = usePropertySpaceSize(form, hasLivingSpace)

const locationComplete = computed(
  () => !!form.street && !!form.house_number && !!form.zip_code && !!form.city && !!form.state,
)

const canLeaveStep = computed(() => {
  switch (step.value) {
    case OWNER_STEP:
      return !!form.owner_id
    case TYPE_STEP:
      return !!form.estate_object_type
    case LOCATION_STEP:
      return locationComplete.value
    default:
      return true
  }
})

const autoAdvancing = computed(() => [OWNER_STEP, TYPE_STEP].includes(step.value))

const showCompanyDialog = ref(false)

const selectOwner = (option: PartyOption): void => {
  form.owner_id = option.id
  form.owner_type = option.type

  advance()
}

const selectCreatedCompany = (companyId: string): void => {
  form.owner_id = companyId
  form.owner_type = 'company'

  advance()
}

const trackedFields = computed(() => [
  { label: t('fields.owner'), filled: !!form.owner_id },
  { label: t('fields.estate_object_type'), filled: !!form.estate_object_type },
  { label: t('fields.street'), filled: !!form.street },
  { label: t('fields.house_number'), filled: !!form.house_number },
  { label: t('fields.zip_code'), filled: !!form.zip_code },
  { label: t('fields.city'), filled: !!form.city },
  { label: t('fields.state'), filled: !!form.state },
  { label: spaceLabel.value, filled: spaceSize.value !== null },
  { label: t('fields.number_of_units'), filled: form.number_of_units !== null },
])

const stepLabels = computed(() => [
  t('steps.owner'),
  t('steps.type'),
  t('steps.location'),
  t('steps.units'),
  t('steps.done'),
])

const financingRequestQuery = computed(() => (props.forFinancingRequest ? { for_financing_request: 1 } : {}))

const leaveHref = computed(() =>
  props.forFinancingRequest
    ? FinancingRequestWizardController.create({ query: { origin: 'portfolio' } })
    : EstateObjectsController.index(),
)

const next = () => {
  if (!canLeaveStep.value) {
    return
  }

  if (step.value === LAST_FORM_STEP) {
    form.post(EstateObjectsController.storeBasics({ query: financingRequestQuery.value }).url, {
      preserveScroll: true,
      onError: showFirstRejectedStep,
    })

    return
  }

  advance()
}
</script>

<template>
  <Head :title="t('heading')" />

  <AppContextPanel>
    <PropertyCreateAssistant :fields="trackedFields" />
  </AppContextPanel>

  <div class="mb-4">
    <Link :href="leaveHref">
      <FinestButton
        :label="forFinancingRequest ? t('backToFinancingRequest') : t('back')"
        icon="pi pi-arrow-left"
        variant="ghost"
        size="sm" />
    </Link>
  </div>

  <div class="mb-6">
    <p class="section-label">{{ t('eyebrow') }}</p>
    <div class="flex items-center gap-2">
      <h1 class="page-title">{{ t('heading') }}</h1>
      <PageTitleHelp :text="t('subtitle')" />
    </div>
  </div>

  <FinestStepNav :steps="stepLabels" :current="step" :locked="locked" @select="goBackTo" />

  <PersonOrCompanyStep
    v-if="step === OWNER_STEP"
    role="owner"
    :options="ownerOptions"
    :selected-id="form.owner_id"
    :errors="stepErrors"
    @select="selectOwner"
    @add-company="showCompanyDialog = true" />

  <PropertyTypeStep
    v-else-if="step === TYPE_STEP"
    v-model="form.estate_object_type"
    :types="estateObjectTypes"
    :errors="stepErrors"
    @select="advance" />

  <PropertyLocationStep
    v-else-if="step === LOCATION_STEP"
    v-model:place-id="form.google_place_id"
    v-model:street="form.street"
    v-model:house-number="form.house_number"
    v-model:address-addition="form.address_addition"
    v-model:zip-code="form.zip_code"
    v-model:city="form.city"
    v-model:state="form.state"
    :states="states"
    :errors="stepErrors" />

  <PropertyUnitsStep
    v-else-if="step === LAST_FORM_STEP"
    v-model:space-size="spaceSize"
    v-model:number-of-units="form.number_of_units"
    :space-label="spaceLabel"
    :errors="stepErrors" />

  <PropertyCreateDone
    v-else
    :estate-object-id="created?.id"
    :title="created?.title ?? ''"
    :for-financing-request="forFinancingRequest" />

  <div v-if="step !== DONE_STEP" class="mt-6 flex flex-wrap items-center justify-between gap-3">
    <FinestButton
      v-if="canReturnTo(step - 1)"
      variant="ghost"
      :label="t('previous')"
      icon="pi pi-arrow-left"
      @click="goBackTo(step - 1)" />
    <Link v-else-if="!created" :href="leaveHref">
      <FinestButton variant="ghost" :label="t('cancel')" />
    </Link>
    <span v-else />

    <FinestButton
      v-if="!autoAdvancing"
      variant="primary"
      :label="step === LAST_FORM_STEP ? t('save') : t('next')"
      icon="pi pi-arrow-right"
      icon-pos="right"
      :disabled="!canLeaveStep || form.processing"
      @click="next" />
  </div>

  <CompanyCreateDialog
    v-model:visible="showCompanyDialog"
    :show-trigger="false"
    return-to="estate_object_owner"
    @created="selectCreatedCompany" />
</template>

<i18n>
{
  "de": {
    "eyebrow": "Portfolio · Immobilie hinzufügen",
    "heading": "Neues Objekt erfassen",
    "subtitle": "Starte schlank: Objektart und Adresse genügen zum Anlegen. Alles Weitere ergänzt du Schritt für Schritt – der Assistent zeigt dir, was noch offen ist.",
    "back": "Zum Portfolio",
    "backToFinancingRequest": "Zurück zur Finanzierungsanfrage",
    "next": "Weiter",
    "previous": "Zurück",
    "cancel": "Abbrechen",
    "save": "Grunddaten speichern",
    "steps": {
      "owner": "Eigentümer",
      "type": "Objektart",
      "location": "Lage",
      "units": "Einheiten & Miete",
      "done": "Fertig"
    },
    "fields": {
      "owner": "Eigentümer",
      "estate_object_type": "Objektart",
      "street": "Straße",
      "house_number": "Hausnummer",
      "zip_code": "PLZ",
      "city": "Ort",
      "state": "Bundesland",
      "living_space_size": "Wohnfläche",
      "business_space_size": "Gewerbefläche",
      "number_of_units": "Anzahl Einheiten"
    }
  },
  "en": {
    "eyebrow": "Portfolio · Add property",
    "heading": "Capture a new property",
    "subtitle": "Start lean: property type and address are enough to create it. Everything else you add step by step – the assistant shows what is still open.",
    "back": "Back to portfolio",
    "backToFinancingRequest": "Back to the financing request",
    "next": "Next",
    "previous": "Back",
    "cancel": "Cancel",
    "save": "Save basic data",
    "steps": {
      "owner": "Owner",
      "type": "Property type",
      "location": "Location",
      "units": "Units & rent",
      "done": "Done"
    },
    "fields": {
      "owner": "Owner",
      "estate_object_type": "Property type",
      "street": "Street",
      "house_number": "House number",
      "zip_code": "Postal code",
      "city": "City",
      "state": "Federal state",
      "living_space_size": "Living space",
      "business_space_size": "Commercial space",
      "number_of_units": "Number of units"
    }
  }
}
</i18n>
